import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';

interface CredentialDetail {
  id: string;
  title: string;
  recipientName?: string;
  status?: string;
  recipient?: { name: string; email: string };
  template?: { name: string };
  issuedAt?: string;
  verificationCode?: string;
  verificationUrl?: string;
}

const STATUS: Record<string, { label: string; tone: string; dot: string }> = {
  issued: { label: 'Emitida', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  revoked: { label: 'Revocada', tone: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  expired: { label: 'Expirada', tone: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
};

function formatDate(iso: string, seconds = false) {
  const d = new Date(iso);
  return `${d.toLocaleDateString('es')}${seconds ? ',' : ''} ${d.toLocaleTimeString('es', {
    hour: '2-digit',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
    hour12: false,
  })}`;
}

function StatusBadge({ status, label }: { status: string; label: string }) {
  const { tone, dot } = STATUS[status] ?? STATUS.issued;
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${tone}`}>
      <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

export function CredentialShowPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['credentials', id],
    queryFn: () => api<CredentialDetail>(`/credentials/${id}`),
    enabled: Boolean(id),
  });

  const recipient = data?.recipientName ?? data?.recipient?.name ?? '—';
  const status = data?.status ?? 'issued';
  const statusInfo = STATUS[status] ?? STATUS.issued;
  const [copied, setCopied] = useState(false);

  function copyCode() {
    if (!data?.verificationCode) return;
    void navigator.clipboard.writeText(data.verificationCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="lms-credential-show lms-site-admin-hub-page font-sans">
      <div className="mb-8 flex items-start gap-4">
        <HubBackLink to="/credenciales" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">{data?.title ?? 'Credencial'}</h1>
          <p className="mt-1 text-dash-on-surface-variant">{recipient}</p>
        </div>
      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <div className="space-y-6">
          <section className="rounded-xl border border-[#e2e2e5] bg-white p-6 shadow-sm sm:p-8">
            <div className="space-y-6">
              <div className="flex flex-col justify-between gap-2 border-b border-gray-100 pb-4 sm:flex-row sm:items-center">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Ficha Oficial</span>
                  <h2 className="mt-0.5 text-lg font-bold text-gray-900">Detalle de la Credencial</h2>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-gray-500">ID Credencial:</span>
                  <span className="rounded border border-gray-200 bg-gray-100 px-2 py-0.5 font-mono text-xs font-medium text-gray-700">
                    #{data?.verificationCode?.slice(0, 4) ?? '—'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Destinatario</span>
                  <p className="text-sm font-semibold text-gray-900">{recipient}</p>
                  {data?.recipient?.email && <p className="text-xs text-gray-500">{data.recipient.email}</p>}
                </div>
                <div className="space-y-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Plantilla</span>
                  <div className="flex items-center space-x-1.5 pt-0.5">
                    <span className="material-symbols-outlined text-[18px] text-gray-400" aria-hidden="true">
                      badge
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{data?.template?.name ?? '—'}</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Estado</span>
                  <div className="pt-0.5">
                    <StatusBadge status={status} label={`${statusInfo.label} (${status})`} />
                  </div>
                  {data?.issuedAt && <p className="mt-1 text-xs text-gray-500">{formatDate(data.issuedAt)} hrs</p>}
                </div>
              </div>

              <div className="flex flex-col items-stretch justify-between gap-4 border-t border-gray-100 pt-5 md:flex-row md:items-center">
                <div className="space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Código Único</span>
                  <div className="flex items-center space-x-2 pt-1">
                    <div className="flex items-center space-x-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 font-mono text-sm font-bold tracking-wider text-gray-900">
                      <span className="material-symbols-outlined text-[18px] text-gray-400" aria-hidden="true">
                        key
                      </span>
                      <span>{data?.verificationCode ?? '—'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={copyCode}
                      disabled={!data?.verificationCode}
                      className="inline-flex items-center justify-center space-x-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900 disabled:opacity-60"
                    >
                      <span
                        className={`material-symbols-outlined text-[15px] ${copied ? 'text-emerald-600' : 'text-gray-500'}`}
                        aria-hidden="true"
                      >
                        {copied ? 'check' : 'content_copy'}
                      </span>
                      <span>{copied ? 'Copiado' : 'Copiar'}</span>
                    </button>
                  </div>
                </div>
                {data?.verificationUrl && (
                  <div className="self-end md:self-auto">
                    <a
                      href={data.verificationUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-10 items-center justify-center space-x-2 rounded-lg bg-[#000256] px-5 text-sm font-medium text-white shadow-sm transition-all hover:bg-[#001863] hover:shadow"
                    >
                      <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                        fact_check
                      </span>
                      <span>Verificar credencial</span>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="overflow-hidden rounded-xl border border-[#e2e2e5] bg-white shadow-sm">
            <div className="border-b border-gray-100 px-6 py-4">
              <h3 className="text-base font-bold text-gray-900">Historial de la credencial</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-[#e2e2e5] bg-gray-50">
                    {['Fecha', 'Acción', 'Por'].map((h) => (
                      <th key={h} scope="col" className="px-6 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  <tr className="transition-colors hover:bg-gray-50/70">
                    <td className="whitespace-nowrap px-6 py-3.5 font-medium text-gray-700">
                      <div className="flex items-center space-x-2">
                        <span className="material-symbols-outlined text-[18px] text-gray-400" aria-hidden="true">
                          calendar_today
                        </span>
                        <span>{data?.issuedAt ? formatDate(data.issuedAt, true) : '—'}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-3.5">
                      <StatusBadge status="issued" label="Emitida" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-3.5 text-gray-700">
                      <div className="flex items-center space-x-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#00227B] text-[10px] font-bold text-white">
                          S
                        </div>
                        <span className="font-medium">Sistema</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
